import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BookOpen, Car, Loader2, Mail, MessageSquareText, Phone } from 'lucide-react';
import { fetchHistory, fetchLearningBooklet, fetchLivret, setLearningSelfAssessment } from '../api/candidate';
import type { LearningBooklet } from '../types';
import { monitorInitials } from '../lib/format';
import { Photo } from '../components/Photos';
import dayjs from '../lib/dates';
import { Accordion, Badge, type BadgeTone, Card, EmptyState, ProgressBar, cx } from '../components/charte';

// Mon livret (refonte du 14/10/2026) : mêmes données et même auto-évaluation qu'avant. Les catégories du
// livret d'apprentissage se replient (liste courte sur téléphone), la progression globale est en tête.

// Niveaux d'auto-évaluation de l'élève (smileys).
const SELF_LEVELS = [
  { value: 1, icon: '🙁', label: 'Peu sûr' },
  { value: 2, icon: '😐', label: 'À confirmer' },
  { value: 3, icon: '🙂', label: 'À l\'aise' },
] as const;

// Progression posée par le staff / le moniteur (lecture seule).
const PROGRESSION_LABEL: Record<string, string> = {
  aborde: 'Abordé',
  traite: 'Traité',
  assimile: 'Assimilé',
};

// Progression = part des compétences « Assimilé », posée par le moniteur ou l'auto-école (14/10/2026).
// Avant, elle était calculée sur l'auto-évaluation de l'élève : « À l'aise » partout affichait 100 %.
// Le ressenti de l'élève reste affiché à côté de chaque compétence, sans compter dans la progression.
function assimilated(items: { progression: string | null }[]) {
  return items.filter((it) => it.progression === 'assimile').length;
}
function validatedPct(items: { progression: string | null }[]): number {
  return items.length === 0 ? 0 : Math.round((assimilated(items) / items.length) * 100);
}

function LearningBookletCard() {
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['candidate', 'learning-booklet'],
    queryFn: fetchLearningBooklet,
  });

  const mutation = useMutation({
    mutationFn: (vars: { itemCode: string; level: number | null }) => setLearningSelfAssessment(vars.itemCode, vars.level),
    onSuccess: (fresh: LearningBooklet) => {
      queryClient.setQueryData(['candidate', 'learning-booklet'], fresh);
    },
  });

  if (isLoading) {
    return (
      <Card className="flex items-center text-sm text-secondary">
        <Loader2 className="mr-2 animate-spin" size={16} /> Chargement du livret…
      </Card>
    );
  }
  if (isError || !data) return null;

  const allItems = data.categories.flatMap((c) => c.items);
  const overallPct = validatedPct(allItems);

  return (
    <section className="grid gap-3">
      <Card className="flex items-center gap-4">
        <div className="min-w-0 flex-1">
          <h2 className="text-title-sm text-heading">Livret d'apprentissage</h2>
          <p className="mt-0.5 text-xs text-secondary">{data.referentialLabel}</p>
          <p className="mt-3 text-[13px] leading-relaxed text-body">
            Votre moniteur valide chaque compétence : abordé, traité, puis assimilé. Indiquez votre ressenti avec les
            smileys : il le voit et adapte vos leçons.
          </p>
          <div className="mt-3">
            <ProgressBar value={overallPct} label="Compétences assimilées, validées par le moniteur" />
          </div>
          <p className="mt-1.5 text-xs text-secondary">
            {assimilated(allItems)} compétence{assimilated(allItems) > 1 ? 's' : ''} assimilée{assimilated(allItems) > 1 ? 's' : ''} sur {allItems.length}
          </p>
        </div>
        <div className="shrink-0 text-center">
          <div className="text-[32px] font-semibold leading-none tracking-[-1px] text-primary">{overallPct}%</div>
          <div className="mt-1 text-eyebrow uppercase text-muted">Validé</div>
        </div>
      </Card>

      {data.categories.map((cat, index) => {
        return (
          <Accordion
            key={cat.code}
            defaultOpen={index === 0}
            leading={<span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-surface-indigo text-sm font-bold text-primary">{cat.code}</span>}
            title={cat.title}
            meta={
              <span className="shrink-0 text-xs font-bold text-primary-600" title="Compétences assimilées">
                {assimilated(cat.items)}/{cat.items.length}
              </span>
            }
          >
            <ul className="divide-y divide-slate-100">
              {cat.items.map((item) => (
                <li key={item.code} className="flex flex-col gap-2.5 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                  <div className="min-w-0 flex-1">
                    <span className="text-sm text-body">{item.label}</span>
                    {item.progression && (
                      <Badge tone="todo" className="ml-2 align-middle">
                        {PROGRESSION_LABEL[item.progression]}
                      </Badge>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-1.5" role="group" aria-label={`Mon ressenti : ${item.label}`}>
                    <span className="mr-1 text-[11px] font-medium text-muted">Mon ressenti</span>
                    {SELF_LEVELS.map((lvl) => {
                      const active = item.selfAssessment === lvl.value;
                      return (
                        <button
                          key={lvl.value}
                          type="button"
                          title={lvl.label}
                          aria-label={lvl.label}
                          aria-pressed={active}
                          disabled={mutation.isPending}
                          onClick={() => mutation.mutate({ itemCode: item.code, level: active ? null : lvl.value })}
                          className={cx(
                            'inline-flex h-9 items-center gap-1.5 rounded-pill border px-3 text-[13px] font-medium transition disabled:opacity-60',
                            active ? 'border-primary bg-primary text-white' : 'border-line bg-white text-secondary hover:border-primary-200',
                          )}
                        >
                          <span className="text-base leading-none" aria-hidden>
                            {lvl.icon}
                          </span>
                          <span className="max-sm:hidden">{lvl.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </li>
              ))}
            </ul>
          </Accordion>
        );
      })}
    </section>
  );
}

function sectionTone(status: string): BadgeTone {
  const s = status.toLowerCase();
  if (s.includes('acqui') || s.includes('valid') || s === 'done') return 'ok';
  if (s.includes('cours') || s.includes('progress')) return 'todo';
  return 'mute';
}

export default function LivretView() {
  const { data, isLoading } = useQuery({ queryKey: ['candidate', 'livret'], queryFn: fetchLivret });
  const historyQuery = useQuery({ queryKey: ['candidate', 'history'], queryFn: fetchHistory });

  const sections = data?.sections ?? [];
  const lessons = data?.lessons ?? [];
  const bilans = historyQuery.data ?? [];
  const empty = !isLoading && !historyQuery.isLoading && sections.length === 0 && lessons.length === 0 && bilans.length === 0;

  return (
    <div className="grid gap-5 sm:gap-6">
      <div>
        <h1 className="text-title-md text-heading sm:text-title-lg">Mon livret</h1>
        <p className="mt-1 text-sm text-secondary">Votre progression de formation et vos séances enregistrées.</p>
      </div>

      <LearningBookletCard />

      {isLoading ? (
        <Card className="flex items-center text-sm text-secondary">
          <Loader2 className="mr-2 animate-spin" size={16} /> Chargement…
        </Card>
      ) : empty ? (
        <Card>
          <EmptyState icon={BookOpen} title="Livret vide pour le moment" description="Votre livret ne contient pas encore de données. Il se remplira au fil de votre formation." />
        </Card>
      ) : (
        <>
          {sections.length > 0 && (
            <Card>
              <h2 className="text-title-sm text-heading">Progression</h2>
              <div className="mt-4 grid gap-2.5 sm:grid-cols-2">
                {sections.map((s) => (
                  <div key={s.id} className="flex items-center justify-between gap-3 rounded-row bg-surface-light px-4 py-3">
                    <span className="truncate text-sm text-body">{s.type}</span>
                    <Badge tone={sectionTone(s.status)}>{s.status}</Badge>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {bilans.length > 0 && (
            <Card>
              <div className="flex items-center gap-2">
                <MessageSquareText className="text-primary" size={18} aria-hidden />
                <h2 className="text-title-sm text-heading">Retours de vos moniteurs</h2>
              </div>
              <ul className="mt-4 divide-y divide-slate-100">
                {bilans.map((b) => (
                  <li key={b.id} className="flex items-start gap-3 py-3.5 first:pt-0 last:pb-0">
                    <span className="relative grid h-9 w-9 shrink-0 place-items-center overflow-hidden rounded-full bg-avatar text-xs font-semibold text-primary-700">
                      {monitorInitials(b.instructorName)}
                      <Photo url={b.instructorPhotoUrl} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                        <span className="text-sm font-semibold text-heading">{dayjs(b.date).format('ddd D MMM YYYY')}</span>
                        {b.instructorName && <span className="text-xs text-secondary">{b.instructorName}</span>}
                        {b.rating != null && (
                          <span className="text-xs font-medium text-amber-500" aria-label={`${b.rating} sur 5`}>
                            {'★'.repeat(b.rating)}
                            <span className="text-slate-200">{'★'.repeat(Math.max(0, 5 - b.rating))}</span>
                          </span>
                        )}
                      </div>
                      {b.summary && <p className="mt-0.5 text-sm text-body">« {b.summary} »</p>}
                      {(b.instructorPhone || b.instructorEmail) && (
                        <div className="mt-1.5 flex flex-wrap gap-2">
                          {b.instructorPhone && (
                            <a href={`tel:${b.instructorPhone}`} className="inline-flex items-center gap-1.5 rounded-pill bg-surface-indigo px-2.5 py-1 text-xs font-medium text-primary-700 hover:bg-primary-50">
                              <Phone size={12} aria-hidden />
                              {b.instructorPhone}
                            </a>
                          )}
                          {b.instructorEmail && (
                            <a href={`mailto:${b.instructorEmail}`} className="inline-flex max-w-full items-center gap-1.5 truncate rounded-pill bg-surface-indigo px-2.5 py-1 text-xs font-medium text-primary-700 hover:bg-primary-50">
                              <Mail size={12} aria-hidden />
                              {b.instructorEmail}
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {lessons.length > 0 && (
            <Card>
              <h2 className="text-title-sm text-heading">Séances enregistrées</h2>
              <ul className="mt-4 divide-y divide-slate-100">
                {lessons.map((l) => (
                  <li key={l.id} className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
                    <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-surface-indigo text-primary">
                      <Car size={16} aria-hidden />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-heading">{dayjs(l.startsAt).format('ddd D MMM YYYY · HH:mm')}</p>
                      <p className="text-xs text-secondary">
                        {l.type ?? 'Séance'} · {Math.round(l.durationMinutes)} min
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
